/**
 * @module free-shipping-bar/eligibility
 *
 * Vendor free-shipping eligibility — mirrors the `df-vendor-free-shipping`
 * Shopify Function so the cart-drawer message matches the discount the
 * function will apply at checkout.
 */

import { VENDOR_FREE_SHIPPING } from './config.js';
import type { VendorFreeShippingRule } from './config.js';

export type Cart = {
  items?: Array<{ vendor?: string }>;
};

/**
 * Does the cart contain at least one line item from `vendor`?
 * Exact, case-sensitive, trimmed match — same as the extension's
 * `cartLinesIncludeVendor`.
 * @param {{items?: Array<{vendor?: string}>}|null|undefined} cart - /cart.js payload
 * @param {string} vendor
 * @returns {boolean}
 */
export function cartHasVendor(cart: Cart | null | undefined, vendor: string): boolean {
  return (
    Array.isArray(cart?.items) && cart.items.some((item) => (item.vendor || '').trim() === vendor)
  );
}

/**
 * The shopper's market country (ISO alpha-2, uppercased). Pre-checkout there
 * is no delivery address, so the selected market country is the best proxy.
 * @returns {string} e.g. 'DE', or '' when unknown
 */
export function getMarketCountryCode(): string {
  const code = window.Shopify?.localization_custom?.country?.iso_code || '';
  return String(code).trim().toUpperCase();
}

/**
 * Is this cart eligible for vendor free shipping in the current market?
 * @param {{items?: Array<{vendor?: string}>}|null|undefined} cart - /cart.js payload
 * @param {Array<{vendor: string, markets?: string[], allCountries?: boolean}>} [config]
 * @returns {boolean}
 */
export function qualifiesForVendorFreeShipping(
  cart: Cart | null | undefined,
  config: VendorFreeShippingRule[] = VENDOR_FREE_SHIPPING,
): boolean {
  const countryCode = getMarketCountryCode();
  if (!countryCode) return false;

  return config.some(
    (rule) =>
      (rule.allCountries || (rule.markets || []).includes(countryCode)) &&
      cartHasVendor(cart, rule.vendor),
  );
}
